import React, { useState } from 'react';
import { Card, CardHeader, CardContent } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/badge';
import { Icon } from '@/shared/icons';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { useInvoices } from '../hooks';
import { InvoiceDetailDrawer } from './InvoiceDetailDrawer';
import type { CreditInvoice } from '../types';
import { formatDateSafe, type DateInput } from '../utils/formatDateSafe';

const formatDate = (d: DateInput) =>
  formatDateSafe(d, { day: '2-digit', month: 'short', year: 'numeric' });

const formatNumber = (n: number) =>
  new Intl.NumberFormat('pt-BR').format(n);

const formatCurrency = (amount: number, currency = 'BRL') =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(amount);

/**
 * List of credit invoices with click-to-detail.
 */
export const InvoicesPage: React.FC = () => {
  const { invoices, isLoading } = useInvoices();
  const [selectedCycle, setSelectedCycle] = useState<number | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton width="200px" height={24} />
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} variant="rectangular" height="64px" />
        ))}
      </div>
    );
  }

  if (!invoices || invoices.length === 0) {
    return (
      <Card>
        <CardContent className="px-4 pb-4 pt-8">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
              style={{ backgroundColor: 'var(--theme-card-border)' }}
            >
              <Icon name="file-text" className="h-6 w-6" style={{ color: 'var(--theme-muted-text)' }} />
            </div>
            <p className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
              Nenhuma fatura encontrada
            </p>
            <p className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)' }}>
              As faturas aparecerao aqui apos o primeiro ciclo
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2 py-3 px-4">
          <div
            className="grid gap-3 text-xs font-medium"
            style={{
              color: 'var(--theme-muted-text)',
              gridTemplateColumns: '48px 1fr 80px 100px 100px 140px',
            }}
          >
            <span>Ciclo</span>
            <span>Período</span>
            <span>Status</span>
            <span className="text-right">Valor</span>
            <span className="text-right">Saldo Final</span>
            <span className="text-right">Movimentacao</span>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="space-y-0">
            {invoices.map((invoice: CreditInvoice, index: number) => (
              <div
                key={invoice.cycleNumber}
                className={`grid gap-3 items-center py-3 cursor-pointer transition-colors rounded-lg px-2 -mx-2 ${index > 0 ? 'border-t' : ''}`}
                style={{
                  gridTemplateColumns: '48px 1fr 80px 100px 100px 140px',
                  ...(index > 0 ? { borderColor: 'var(--theme-card-border)' } : {}),
                }}
                onClick={() => setSelectedCycle(invoice.cycleNumber)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <span className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                  #{invoice.cycleNumber}
                </span>
                <span className="text-sm whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }}>
                  {formatDate(invoice.period.start)} - {formatDate(invoice.period.end)}
                </span>
                <div>
                  <Badge
                    variant={invoice.status === 'closed' ? 'success' : invoice.status === 'cancelled' ? 'destructive' : 'warning'}
                    dot
                  >
                    {invoice.status === 'closed' ? 'Fechada' : invoice.status === 'cancelled' ? 'Cancelada' : 'Aberta'}
                  </Badge>
                </div>
                <span className="text-sm text-right" style={{ color: 'var(--theme-card-text)' }}>
                  {invoice.payment ? formatCurrency(invoice.payment.total, invoice.payment.currency) : '-'}
                </span>
                <span className="text-sm text-right font-medium" style={{ color: 'var(--theme-card-text)' }}>
                  {formatNumber(invoice.summary.balanceAfter)}
                </span>
                <div className="text-right text-sm">
                  <span style={{ color: '#10b981' }}>+{formatNumber(invoice.summary.totalCreditsIn)}</span>
                  {' / '}
                  <span style={{ color: '#ef4444' }}>-{formatNumber(invoice.summary.totalCreditsOut)}</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Detail drawer */}
      <InvoiceDetailDrawer
        isOpen={selectedCycle !== null}
        onClose={() => setSelectedCycle(null)}
        cycleNumber={selectedCycle}
      />
    </div>
  );
};

InvoicesPage.displayName = 'InvoicesPage';
